import { action, computed } from '@ember/object';
import { isBlank, isEmpty } from '@ember/utils';
import AbstractCondition from './abstract';
import { addError } from './abstract';
import { isValidTimestamp } from '@customerio/fly-components/utils/validators/timestamp';
import { IdentifierKey } from 'cio/utils/identifiers';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import {
  copyJsonConditionToOriginalCondition,
  hasAnyJsonConditions,
  JsonAttributeOperator,
  JsonConditionItem,
  stringToConditionItems,
} from 'cio/utils/conditions/json-attributes';
import { tracked } from '@glimmer/tracking';

const UNARY_OPERATORS = ['exists', '!exists', 'timestamp', '!timestamp', 'email_valid', '!email_valid'];
const RELATIVE_TIMESTAMP_OPERATORS = ['timestamp_lt', 'timestamp_gt'];
const ABSOLUTE_TIMESTAMP_OPERATORS = ['timestamp_lta', 'timestamp_gta'];
const VALUE_OPERATORS = ['eq', '!eq', 'gt', 'lt', 'contains', '!contains', 'contained_in_array', '!contained_in_array'];
const TWO_VALUE_OPERATORS = ['bw', 'timestamp_bw'];
const INVALID_DATE_OPERATOR_NAMES = [...Object.values(IdentifierKey), 'unsubscribed'];
const CREATED_IN_CIO_AT_OPERATORS = [
  'timestamp',
  '!timestamp',
  ...RELATIVE_TIMESTAMP_OPERATORS,
  ...ABSOLUTE_TIMESTAMP_OPERATORS,
  ...TWO_VALUE_OPERATORS,
];

const COMPARABLE_OPERATORS = [
  'eq',
  '!eq',
  'gt',
  'lt',
  'contains',
  '!contains',
  'contained_in_array',
  '!contained_in_array',
];

export const ago = 'ago';
export const fromNow = 'from now';

export function isBinaryOperator(operator: string | null) {
  return !UNARY_OPERATORS.includes(operator as string);
}

export function isRelativeTimestampOperator(operator: string | null) {
  return RELATIVE_TIMESTAMP_OPERATORS.includes(operator as string);
}

export function isAbsoluteTimestampOperator(operator: string | null) {
  return ABSOLUTE_TIMESTAMP_OPERATORS.includes(operator as string);
}

export function isTwoValueOperator(operator: string | null) {
  return TWO_VALUE_OPERATORS.includes(operator as string);
}

export function canSelectComparisonOperator(operator: string | null) {
  return COMPARABLE_OPERATORS.includes(operator as string);
}

class AttributeCondition extends AbstractCondition {
  type = 'attribute';

  trackedAttributes = ['name', 'operator', 'comparisonOperator', 'value', 'value2', 'timestampValue', 'timeDirection'];

  name = '';
  operator = 'eq';
  comparisonOperator = 'value';
  value: string | null = null;
  value2: string | null = null;
  timestampValue = '1';
  timeDirection = fromNow;
  isConversionEventAttribute = false;

  @computed('operator', 'value', 'value2', 'timestampValue')
  get hasValue() {
    let { operator, value, value2, timestampValue } = this;

    if (!isBinaryOperator(operator)) {
      return true;
    }

    if (isTwoValueOperator(operator)) {
      return !isBlank(value) && !isBlank(value2);
    }

    if (isRelativeTimestampOperator(operator)) {
      return !isBlank(timestampValue);
    }

    return !isBlank(value);
  }

  @computed('changedAttributes.[]', 'childCondition.isDirty')
  get isDirty(): boolean {
    return this.changedAttributes.length > 0 || (this.childCondition?.isDirty ?? false);
  }

  @computed(
    'isDirty',
    'shouldValidate',
    'name',
    'operator',
    'value',
    'value2',
    'timestampValue',
    'type',
    'comparisonOperator',
    'arrayConditionType',
  )
  get errors(): Record<string, string[]> {
    let errors = {};

    if (!this.shouldValidate) {
      return errors;
    }

    let { operator, value, value2, name, timestampValue } = this;
    if (
      ['attribute', 'collection_attribute'].includes(this.type) &&
      name === CREATED_IN_CIO_AT &&
      !CREATED_IN_CIO_AT_OPERATORS.includes(operator)
    ) {
      addError(errors, 'operator', 'must be a timestamp');
    }

    if (isBlank(name) && this.arrayConditionType !== 'value') {
      addError(errors, 'name', "can't be blank");
    }

    if (VALUE_OPERATORS.includes(operator)) {
      if (isBlank(value) && this.type !== 'event_attribute') {
        addError(errors, 'value', "can't be blank");
      }
    } else if (operator === 'bw') {
      if (isBlank(value)) {
        addError(errors, 'value', "can't be blank");
      }

      if (isBlank(value2)) {
        addError(errors, 'value2', "can't be blank");
      }
    } else if (operator === 'timestamp_bw') {
      if (!isValidTimestamp(value as unknown as string)) {
        addError(errors, 'value', 'should be a timestamp');
      }

      if (!isValidTimestamp(value2 as unknown as string)) {
        addError(errors, 'value2', 'should be a timestamp');
      }
    } else if (operator === 'email_valid' || operator === '!email_valid') {
      if (name !== 'email') {
        addError(errors, 'name', 'name must be email for this operator');
      }
    } else if (isRelativeTimestampOperator(operator)) {
      if (!Number.isInteger(Number(timestampValue))) {
        addError(errors, 'timestampValue', 'should be an integer');
      }
    } else if (isAbsoluteTimestampOperator(operator)) {
      if (!isValidTimestamp(value as unknown as string)) {
        addError(errors, 'value', 'should be a timestamp');
      }
    }

    if (
      (this.changedAttributes.includes('name') || this.changedAttributes.includes('operator')) &&
      this.disallowDateOperators &&
      this.operator.includes('timestamp')
    ) {
      addError(errors, 'operator', `${this.name} can't be a timestamp`);
    }

    return errors;
  }

  @computed('errors', 'editableCondition.hasErrors', 'childCondition.hasErrors')
  get hasErrors(): boolean {
    let { errors, editableCondition, childCondition } = this;

    if (Object.keys(errors).length > 0) {
      return true;
    }

    // editableCondition can return this class instance directly if we're not
    // editing conditions with JSON attribute UI support (allowJsonOperators =
    // false)
    if (editableCondition !== this && editableCondition.hasErrors) {
      return true;
    }

    return childCondition?.hasErrors ?? false;
  }

  @computed('name', 'operator')
  get disallowDateOperators() {
    if (!this.changedAttributes.includes('operator') && this.operator.includes('timestamp')) {
      return false;
    }

    return INVALID_DATE_OPERATOR_NAMES.includes(this.name ?? '');
  }

  toString() {
    return 'AttributeCondition';
  }

  _event = '';
  @computed('_event', 'originalCondition.event')
  get event() {
    if (this.originalCondition != null) {
      return this.originalCondition.event;
    }

    return this._event;
  }
  set event(value: string) {
    if (this.originalCondition != null) {
      this.originalCondition.set('event', value);
      return;
    }

    this.set('_event', value);
  }

  // When editing an attribute condition that contains a JSON expression (e.g.
  // "products[].price" as the name field), we split it up into a chain of
  // nested condition editors
  _editableCondition: AttributeCondition | null = null;
  @computed('_editableCondition')
  get editableCondition(): AttributeCondition {
    return this._editableCondition ?? this;
  }
  set editableCondition(condition: AttributeCondition) {
    this.set('_editableCondition', condition);
  }

  @tracked originalCondition: AttributeCondition | null = null;
  @tracked childCondition: AttributeCondition | null = null;
  @tracked parentCondition: AttributeCondition | null = null;
  @tracked arrayConditionType: 'property' | 'value' | null = null;
  @tracked isAdvancedJsonMode = false;

  get jsonDepth(): number {
    if (this.parentCondition == null) {
      return 0;
    }

    return this.parentCondition.jsonDepth + 1;
  }

  get isJsonChild() {
    return this.parentCondition != null;
  }

  @computed('name')
  get hasJSONCondition() {
    return hasAnyJsonConditions(this.name);
  }

  @computed('operator')
  get hasJSONOperator() {
    return (Object.values(JsonAttributeOperator) as string[]).includes(this.operator);
  }

  @action
  duplicate() {
    let duplicate = AttributeCondition.create();
    this.copyAllAttributes(this, duplicate);
    return duplicate;
  }

  @action
  createEditCopy() {
    if (this.type === 'foreign_event_attribute') {
      return;
    }

    let name = this.name ?? '';

    let conditionItemsArr = stringToConditionItems(name).reverse();

    // If there's no or only one level of nesting, we're not dealing with a
    // JSON-like attribute string.
    // Copy the condition as-is for editing and return early.
    if (conditionItemsArr.length === 0 || conditionItemsArr.length === 1) {
      this.editableCondition = this.createCopy();
      return;
    }

    // We don't support expressions like A[].B in basic editing mode. In basic
    // mode, we would support A?.B instead.
    // We also don't support array index access in basic editing, e.g. A[1].B
    // When such conditions are encountered, copy the condition as-is for
    // editing, but switch to "advanced" mode, which shows a larger textarea for
    // the attribute name.
    if (
      conditionItemsArr.some(
        (c) =>
          c.operator === JsonAttributeOperator.ArrayHasPropertyOrValue ||
          c.operator === JsonAttributeOperator.ArrayIndexHasPropertyOrValue,
      )
    ) {
      this.editableCondition = this.createCopy();
      if (this.hasJSONCondition) {
        this.isAdvancedJsonMode = true;
      }
      return;
    }

    // Create a nested tree of conditions, which will be used recursively in
    // nested condition editors
    let conds: AttributeCondition[] = [];

    conditionItemsArr.forEach((item, i) => {
      if (i === 0) {
        conds.push(this.createDeepestChildCondition(item));
      } else {
        conds.push(this.createChildCondition(item, conds[i - 1]));
      }
    });

    conds.forEach((cond, i) => {
      if (i === 0) {
        return;
      }

      conds[i - 1].parentCondition = cond;
    });

    this.editableCondition = conds[conds.length - 1];
  }

  @action
  updateOriginalFromEditableCopy() {
    let { editableCondition } = this;

    if (editableCondition == null) {
      return;
    }
    if (editableCondition.childCondition != null) {
      copyJsonConditionToOriginalCondition(editableCondition, this, { escapeFirstElement: false });
    } else {
      this.copyAllAttributes(editableCondition, this);
    }
  }

  @action
  toggleAdvancedJsonMode() {
    if (this.isAdvancedJsonMode) {
      this.createEditCopy();
    } else {
      this.editableCondition = this.createCopy();
    }
    this.isAdvancedJsonMode = !this.isAdvancedJsonMode;
  }

  @action
  createOrUpdateChildCondition() {
    if (
      this.operator === JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue ||
      this.operator === JsonAttributeOperator.ObjectHasProperty
    ) {
      if (this.childCondition == null) {
        // If there is no child condition yet, add one
        let childCondition = this.createEmptyCondition();

        // Default to "Property" for new "where at least one" conditions
        if (this.operator === JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue) {
          childCondition.set('arrayConditionType', 'property');
        }

        // pass down type of attribute to allow any sort of special handling for conversionEventAttributes
        // e.g we disable is-between comparisons based on this type
        if (this.isConversionEventAttribute) {
          childCondition.set('isConversionEventAttribute', true);
        }
        this.set('childCondition', childCondition);
      } else {
        // If a child condition exists and we selected the "has the property
        // operator", make sure to remove the array condition type
        if (
          this.operator === JsonAttributeOperator.ObjectHasProperty &&
          this.childCondition.arrayConditionType != null
        ) {
          this.childCondition.set('arrayConditionType', null);
        } else if (
          this.operator === JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue &&
          this.childCondition.arrayConditionType == null
        ) {
          this.childCondition.set('arrayConditionType', 'property');
        }
      }
    } else if (
      this.operator !== JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue &&
      this.operator !== JsonAttributeOperator.ObjectHasProperty
    ) {
      // If we're switching away from array or object operators, remove the
      // child condition entirely
      let hasCondition = this.childCondition != null;

      if (hasCondition) {
        this.set('childCondition', null);
      }
    }
  }

  @action
  maybeUpdateParentCondition() {
    if (this.parentCondition != null) {
      this.parentCondition.maybeUpdateParentCondition();
    } else if (this.originalCondition) {
      this.originalCondition.updateOriginalFromEditableCopy();
    }
  }

  @action
  applyUpdates() {
    this.createOrUpdateChildCondition();
    this.maybeUpdateParentCondition();
  }

  @action
  nameStringToJsonItems(): JsonConditionItem[] {
    return stringToConditionItems(this.name);
  }

  protected jsonConditionItemToOverrides(item: JsonConditionItem) {
    let overrides: Record<string, any> = {
      name: item.name,
    };

    if (!isEmpty(item.operator)) {
      overrides.operator = item.operator;
    }

    if (item.arrayConditionType != null) {
      overrides.arrayConditionType = item.arrayConditionType;
    }

    return overrides;
  }

  protected createCopy() {
    return AttributeCondition.create({
      _event: this.event,
      name: this.name,
      operator: this.operator,
      comparisonOperator: this.comparisonOperator,
      value: this.value,
      value2: this.value2,
      timestampValue: this.timestampValue,
      timeDirection: this.timeDirection,
      originalCondition: this,
    });
  }

  protected createEmptyCondition() {
    return AttributeCondition.create({
      _event: this.event,
      parentCondition: this,
    });
  }

  copyAllAttributes(source: AttributeCondition, target: AttributeCondition) {
    target.setProperties({
      _event: this.event,
      name: source.name,
      operator: source.operator,
      comparisonOperator: source.comparisonOperator,
      value: source.value,
      value2: source.value2,
      timestampValue: source.timestampValue,
      timeDirection: source.timeDirection,
    });
  }

  protected createDeepestChildCondition(item: JsonConditionItem) {
    return AttributeCondition.create({
      _event: this.event,
      operator: this.operator,
      comparisonOperator: this.comparisonOperator,
      value: this.value,
      value2: this.value2,
      timestampValue: this.timestampValue,
      timeDirection: this.timeDirection,
      originalCondition: this,
      ...this.jsonConditionItemToOverrides(item),
    });
  }

  protected createChildCondition(item: JsonConditionItem, previousCondition: AttributeCondition) {
    return AttributeCondition.create({
      _event: this.event,
      ...this.jsonConditionItemToOverrides(item),
      childCondition: previousCondition,
      originalCondition: this,
    });
  }
}

export default AttributeCondition;
